import {useEffect, useState} from "react";

import {useQuotes} from "../QuotesContext";
import {onlyDateISOString} from "../../../services/date/formatters";
import {isValidDateObject} from "../../../services/date/validators";
import {differenceInDays, startOfDay, endOfDay} from "date-fns";
import {useBookingVariantsQuery} from "../query";


/**
 * A hook that generates the possible quote variants given the arrival, the departure the number of adults
 * and the number of children.
 *
 * It exclusively considers the available apartment.
 *
 * @param props
 */
const useVariantGenerator = (props) => {
    const {accommodations} = useQuotes();
    const [enabled, setEnabled] = useState(true);
    const {arrival, departure, adults, children, petsNumber, homeId= null, limit = 2} = props;

    const arrivalISO = isValidDateObject(arrival) ? onlyDateISOString(arrival) : null;
    const departureISO = isValidDateObject(departure) ? onlyDateISOString(departure) : null;
    const people = adults + children;
    const nights = isValidDateObject(arrival) && isValidDateObject(departure) ?
        differenceInDays(endOfDay(departure), startOfDay(arrival)) : 0;

    // Query to get the booking variants
    const bookingVariantsQuery = useBookingVariantsQuery(arrivalISO, departureISO, people, petsNumber, homeId);
    const availableApartmentsPrices = bookingVariantsQuery.data?.pricesInfo?.availableApartmentsPrices;

    // When the arrival, the departure, the number of adults or the number of children changes, enable the generator
    useEffect(() => {
        setEnabled(true);
    }, [arrivalISO, departureISO, people, petsNumber]);

    const emptyQuoteItem = {
        apartmentId: "",
        priceCent: null,
        arrival: isValidDateObject(arrival) ? onlyDateISOString(arrival) : null,
        departure: isValidDateObject(departure) ? onlyDateISOString(departure) : null,
    };

    const emptyQuote = {quoteItems: [emptyQuoteItem]};

    const generateVariants = () => {
        if(bookingVariantsQuery.data){
            return bookingVariantsQuery.data.options.slice(0, limit).map(o => ({
                    quoteItems: o.items.map(item => ({
                        apartmentId: item.apartmentId,
                        priceCent: item.priceCent,
                        arrival: onlyDateISOString(arrival),
                        departure: onlyDateISOString(departure),
                    }))
            }))
        } else{
            return [];
        }
    }

    // Update the arrival and departure date of a given set of variants.
    // Useful if the date are changed after the generation of some variant.
    const updateVariantsDate = (quoteVariants) => {
        return quoteVariants.map(quoteVariant => ({
            ...quoteVariant,
            quoteItems: quoteVariant.quoteItems.map(quoteItem => ({
                ...quoteItem,
                arrival: onlyDateISOString(arrival),
                departure: onlyDateISOString(departure),
            }))
        }))
    }

    // Generate the price for the given apartment in the given variant (the apartment is
    // expected not to have the apartment to add).
    // If the variant is empty the price is selected according to the people number.
    // If the variant is not empty the price is selected according to the residual
    // people number. The residual people is computed by subtracting from the people
    // number the people assigned to all the other apartments in the variants.
    // The people assigned to the other apartments is guessed from the price selected
    // for the apartment, if present otherwise it is considered 0.
    const generateApartmentPrice = (apartmentId, quoteVariant) => {
        const apartmentPrices = availableApartmentsPrices?.find(a => a.apartmentId === apartmentId)
        const prices = apartmentPrices?.prices;

        // number of items in the variants. +1 because the given apartment is not yet in the variant
        // it is used to divide the pets fee so cannot be == 0
        const itemsNumbers = quoteVariant.quoteItems.length + 1;

        // Return null if no prices are available for the apartment
        if(!prices || !prices.length > 0){
            return null;
        }

        const selectedApartmentIds = quoteVariant.quoteItems
            .filter(i => i.apartmentId !== apartmentId)
            .map(i => i.apartmentId);
        const selectedApartmentPrices = quoteVariant.quoteItems
            .filter(i => i.apartmentId !== apartmentId)
            .reduce((acc, i) => ({...acc, [i.apartmentId]: i.priceCent}), {});

        // Guess the people assigned to each apartment by looking at the price selected for the apartment.
        // The price object ({people: , priceCent:}) which price is closest to the selected price of the apartment
        // is considered the right one.
        const apartmentsPeople = selectedApartmentIds.reduce((acc, otherId) => {
            let selectedPrice = selectedApartmentPrices[otherId];
            const otherApartmentPrices = availableApartmentsPrices.find(a => a.apartmentId === otherId)
            const otherPrices = otherApartmentPrices?.prices;
            let people = 0;
            if (selectedPrice && otherPrices && otherPrices.length > 0) {
                // remove the pets fee from the selected price
                if(otherApartmentPrices.petsFeeCent && otherApartmentPrices.petsFeeCent > 0){
                    selectedPrice = selectedPrice - otherApartmentPrices.petsFeeCent / itemsNumbers;
                }

                // find the price with the number of people closest to the selected price
                const closestPrice = otherPrices
                    .sort((a, b) => b.people - a.people)  //  sort from the larger number of people to the smallest
                    .reduce((closest, p) => (
                        Math.abs(p.priceCent - selectedPrice) < Math.abs(closest.priceCent - selectedPrice) ? p : closest
                    ), otherPrices[otherPrices.length - 1]);
                people = closestPrice.people;
            }
            return {...acc, [otherId]: people};
        }, {});

        // Compute the residual people and the target people used to select the price
        const residualPeople = people - Object.values(apartmentsPeople).reduce((acc, p) => acc + p, 0);
        const maxPeople = Math.max(...prices.map(p => p.people));
        const minPeople = Math.min(...prices.map(p => p.people));
        const targetPeople = Math.min(Math.max(residualPeople, minPeople), maxPeople);

        let priceCent = prices.find(p => p.people === targetPeople)?.priceCent;
        if (priceCent && priceCent > 0 && apartmentPrices?.petsFeeCent > 0){
            return priceCent + Math.ceil(apartmentPrices.petsFeeCent / itemsNumbers / 100) * 100;
        }
        return prices.find(p => p.people === targetPeople)?.priceCent;
    }

    return {
        isLoading: accommodations.isLoading || bookingVariantsQuery.isLoading,
        enabled,
        setEnabled,
        bookingVariantsQuery,
        emptyQuoteItem,
        emptyQuote,
        arrival,
        departure,
        adults,
        children,
        nights,
        people,
        generateApartmentPrice,
        generateVariants,
        updateVariantsDate
    }
}

export default useVariantGenerator;